import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Megaphone, Image, Video, FileText, Target, DollarSign,
  Calendar, ChevronRight, ChevronLeft, Check, Loader2,
  MapPin, Users, Music, Eye, Sparkles, Zap, Star,
  Clock, Globe, TrendingUp, CheckCircle
} from 'lucide-react';
import { API_BASE_URL as API } from '../config/api';

const AD_TYPE_OPTIONS = [
  { id: 'banner_ad', name: 'Banner Ad', icon: Image, desc: 'Image banner displayed in feed' },
  { id: 'video_ad', name: 'Video Ad', icon: Video, desc: 'Video content with autoplay' },
  { id: 'sponsored_post', name: 'Sponsored Post', icon: FileText, desc: 'Native post in the feed' },
  { id: 'audio_spotlight', name: 'Audio Spotlight', icon: Music, desc: 'Promote a track/song' },
  { id: 'profile_promo', name: 'Profile Promo', icon: Users, desc: 'Promote your profile' },
  { id: 'event_promo', name: 'Event Promo', icon: Calendar, desc: 'Promote an event' },
];

export default function CampaignCreatePage() {
  const { user, getAuthHeaders, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [packages, setPackages] = useState([]);
  const [ctaOptions, setCTAOptions] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableGenres, setAvailableGenres] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [adType, setAdType] = useState('');
  const [content, setContent] = useState({});
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customBudget, setCustomBudget] = useState(10);
  const [targetRoles, setTargetRoles] = useState([]);
  const [targetGenres, setTargetGenres] = useState([]);
  const [targetLocations, setTargetLocations] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchPackages();
  }, [isAuthenticated]);

  const fetchPackages = async () => {
    try {
      const res = await fetch(`${API}/api/ads/packages`);
      if (res.ok) {
        const data = await res.json();
        setPackages(data.packages || []);
        setCTAOptions(data.cta_options || []);
        setAvailableLocations(data.target_locations || []);
        setAvailableRoles(data.target_roles || []);
        setAvailableGenres(data.genres || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPackage || !title.trim() || !adType) {
      toast.error('Please complete all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const isCustom = selectedPackage.is_custom;
      const body = {
        title,
        description,
        ad_type: adType,
        content,
        package_id: selectedPackage.package_id,
        target_roles: targetRoles,
        target_genres: targetGenres,
        target_locations: targetLocations,
      };
      if (isCustom) body.custom_budget_cents = customBudget * 100;

      const res = await fetch(`${API}/api/advertiser/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.checkout_url) {
          toast.success('Redirecting to Stripe checkout...');
          window.location.href = data.checkout_url;
          return;
        }
        toast.success('Campaign created!');
        navigate('/ads/manager');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create campaign');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const updateContent = (field, value) => setContent(prev => ({ ...prev, [field]: value }));
  const toggle = (arr, setter, val) => setter(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);

  const canProceed = () => {
    switch (step) {
      case 1: return title.trim() && adType;
      case 2: return content.headline;
      case 3: return !!selectedPackage;
      case 4: return true;
      default: return false;
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const STEPS = ['Ad Details', 'Content', 'Package', 'Targeting', 'Review'];

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="campaign-create-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-orange-500/20 rounded-lg">
            <Megaphone className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Create Ad Campaign</h1>
            <p className="text-sm text-muted-foreground">Reach the DMV creative community</p>
          </div>
        </div>

        {/* Steps */}
        <div className="flex items-center gap-1 mb-8">
          {STEPS.map((label, idx) => (
            <React.Fragment key={idx}>
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                step > idx + 1 ? 'bg-green-500/20 text-green-400' :
                step === idx + 1 ? 'bg-orange-500/20 text-orange-400' :
                'bg-muted/30 text-muted-foreground'
              }`}>
                {step > idx + 1 ? <Check className="w-3 h-3" /> : <span>{idx + 1}</span>}
                <span className="hidden sm:inline">{label}</span>
              </div>
              {idx < STEPS.length - 1 && <div className={`w-6 h-0.5 ${step > idx + 1 ? 'bg-green-500/40' : 'bg-muted/50'}`} />}
            </React.Fragment>
          ))}
        </div>

        <Card className="p-6" data-testid="campaign-form">
          {/* Step 1: Ad Details */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Ad Details</h2>
              <div className="space-y-2">
                <Label>Campaign Title *</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Summer Music Promo" data-testid="campaign-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description of your campaign goals..." rows={2} />
              </div>
              <div className="space-y-3">
                <Label>Ad Type *</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {AD_TYPE_OPTIONS.map(type => {
                    const Icon = type.icon;
                    return (
                      <div key={type.id} onClick={() => setAdType(type.id)}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${adType === type.id ? 'border-orange-500 bg-orange-500/10' : 'border-border hover:border-border'}`}
                        data-testid={`ad-type-${type.id}`}
                      >
                        <Icon className={`w-5 h-5 mb-1.5 ${adType === type.id ? 'text-orange-400' : 'text-muted-foreground'}`} />
                        <p className="font-medium text-sm">{type.name}</p>
                        <p className="text-xs text-muted-foreground">{type.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Content */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold">Ad Content</h2>
              <div className="space-y-2">
                <Label>Headline *</Label>
                <Input value={content.headline || ''} onChange={e => updateContent('headline', e.target.value)} placeholder="Attention-grabbing headline" maxLength={100} data-testid="content-headline-input" />
              </div>
              <div className="space-y-2">
                <Label>Short Description</Label>
                <Textarea value={content.body_text || ''} onChange={e => updateContent('body_text', e.target.value)} placeholder="Your ad message..." rows={3} maxLength={500} />
              </div>
              {(adType === 'banner_ad' || adType === 'sponsored_post' || adType === 'event_promo' || adType === 'profile_promo') && (
                <div className="space-y-2">
                  <Label>Image URL</Label>
                  <Input value={content.image_url || ''} onChange={e => updateContent('image_url', e.target.value)} placeholder="https://your-image-url.com/image.jpg" data-testid="content-image-input" />
                  <p className="text-xs text-muted-foreground">Recommended: 1200x628px</p>
                </div>
              )}
              {adType === 'video_ad' && (
                <div className="space-y-2">
                  <Label>Video URL</Label>
                  <Input value={content.video_url || ''} onChange={e => updateContent('video_url', e.target.value)} placeholder="https://youtube.com/watch?v=..." />
                </div>
              )}
              <div className="space-y-2">
                <Label>Call-to-Action</Label>
                <div className="flex flex-wrap gap-2">
                  {(ctaOptions.length > 0 ? ctaOptions : ['Listen Now', 'Watch Now', 'Learn More', 'Follow', 'Book Now']).map(cta => (
                    <Badge key={cta} variant={content.cta_text === cta ? 'default' : 'outline'} className="cursor-pointer" onClick={() => updateContent('cta_text', cta)}>{cta}</Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Link Destination</Label>
                <Input value={content.cta_url || ''} onChange={e => updateContent('cta_url', e.target.value)} placeholder="https://your-landing-page.com" />
              </div>
            </div>
          )}

          {/* Step 3: Package Selection */}
          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold">Choose Your Package</h2>
              <div className="space-y-3">
                {packages.filter(p => !p.is_custom).map(pkg => {
                  const isSelected = selectedPackage?.package_id === pkg.package_id;
                  return (
                    <div key={pkg.package_id} onClick={() => setSelectedPackage(pkg)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${isSelected ? 'border-orange-500 bg-orange-500/10' : 'border-border hover:border-border'}`}
                      data-testid={`package-${pkg.package_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-bold">{pkg.name}</p>
                            {pkg.priority >= 70 && <Badge className="bg-orange-500/20 text-orange-400 text-xs">Popular</Badge>}
                            {pkg.priority >= 100 && <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Premium</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">{pkg.description}</p>
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{pkg.duration_hours}h</span>
                            <span className="flex items-center gap-1"><Eye className="w-3 h-3" />Up to {pkg.max_impressions?.toLocaleString()} impressions</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-black text-orange-400">{pkg.price_display}</p>
                          {isSelected && <CheckCircle className="w-5 h-5 text-orange-400 ml-auto mt-1" />}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Custom daily spend */}
                {packages.filter(p => p.is_custom).map(pkg => {
                  const isSelected = selectedPackage?.package_id === pkg.package_id;
                  return (
                    <div key={pkg.package_id} onClick={() => setSelectedPackage(pkg)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${isSelected ? 'border-orange-500 bg-orange-500/10' : 'border-border hover:border-border'}`}
                      data-testid={`package-${pkg.package_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold">{pkg.name}</p>
                          <p className="text-sm text-muted-foreground">{pkg.description}</p>
                        </div>
                        <Zap className="w-5 h-5 text-orange-400" />
                      </div>
                      {isSelected && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <Label>Daily Budget ($)</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <DollarSign className="w-4 h-4 text-muted-foreground" />
                            <Input type="number" value={customBudget} onChange={e => setCustomBudget(Math.max(5, parseInt(e.target.value) || 5))} className="w-32" min={5} data-testid="custom-budget-input" />
                            <span className="text-sm text-muted-foreground">/ day (min $5)</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Targeting */}
          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold">Audience Targeting</h2>
              <p className="text-sm text-muted-foreground">Leave empty for broad audience (all users)</p>
              <div>
                <Label className="mb-2 block flex items-center gap-2"><MapPin className="w-4 h-4" />Locations</Label>
                <div className="flex flex-wrap gap-2">
                  {availableLocations.map(loc => (
                    <Badge key={loc} variant={targetLocations.includes(loc) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggle(targetLocations, setTargetLocations, loc)}>{loc}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-2 block flex items-center gap-2"><Users className="w-4 h-4" />User Roles</Label>
                <div className="flex flex-wrap gap-2">
                  {availableRoles.map(role => (
                    <Badge key={role} variant={targetRoles.includes(role) ? 'default' : 'outline'} className="cursor-pointer capitalize" onClick={() => toggle(targetRoles, setTargetRoles, role)}>{role}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-2 block flex items-center gap-2"><Music className="w-4 h-4" />Genres</Label>
                <div className="flex flex-wrap gap-2">
                  {availableGenres.map(genre => (
                    <Badge key={genre} variant={targetGenres.includes(genre) ? 'default' : 'outline'} className="cursor-pointer capitalize" onClick={() => toggle(targetGenres, setTargetGenres, genre)}>{genre}</Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Review & Pay */}
          {step === 5 && (
            <div className="space-y-5" data-testid="step-review">
              <h2 className="text-lg font-semibold">Review & Pay</h2>
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-3 bg-muted/30">
                  <p className="text-xs text-muted-foreground">Campaign</p>
                  <p className="font-bold">{title}</p>
                  <p className="text-xs text-muted-foreground capitalize mt-1">{adType?.replace('_', ' ')}</p>
                </Card>
                <Card className="p-3 bg-muted/30">
                  <p className="text-xs text-muted-foreground">Package</p>
                  <p className="font-bold">{selectedPackage?.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{selectedPackage?.duration_hours}h duration</p>
                </Card>
                <Card className="p-3 bg-muted/30">
                  <p className="text-xs text-muted-foreground">Targeting</p>
                  <p className="text-sm">{targetLocations.length > 0 ? targetLocations.join(', ') : 'All locations'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{targetRoles.length > 0 ? targetRoles.join(', ') : 'All users'}</p>
                </Card>
                <Card className="p-3 bg-orange-500/10 border-orange-500/30">
                  <p className="text-xs text-muted-foreground">Total</p>
                  <p className="text-2xl font-black text-orange-400">
                    {selectedPackage?.is_custom ? `$${customBudget}.00` : selectedPackage?.price_display}
                  </p>
                </Card>
              </div>

              {/* Ad Preview */}
              <div>
                <Label className="mb-2 block">Ad Preview</Label>
                <Card className="p-4 border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Megaphone className="w-4 h-4 text-orange-400" />
                    <Badge variant="outline" className="text-xs">Sponsored</Badge>
                  </div>
                  {content.image_url && <img src={content.image_url} alt="" className="w-full h-40 object-cover rounded-lg mb-3" />}
                  <h3 className="font-bold text-lg">{content.headline || 'Your Headline'}</h3>
                  {content.body_text && <p className="text-sm text-muted-foreground mt-1">{content.body_text}</p>}
                  {content.cta_text && (
                    <Button className="mt-3 bg-orange-500 hover:bg-orange-600" size="sm">{content.cta_text}</Button>
                  )}
                </Card>
              </div>

              <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 h-12 text-lg font-bold" data-testid="submit-campaign-btn">
                {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : <><DollarSign className="w-5 h-5 mr-2" />Pay {selectedPackage?.is_custom ? `$${customBudget}.00` : selectedPackage?.price_display} & Launch</>}
              </Button>
              <p className="text-xs text-muted-foreground text-center">Secure payment via Stripe. Your ad goes live immediately after payment.</p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-border">
            <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : navigate('/ads/manager')}>
              <ChevronLeft className="w-4 h-4 mr-2" />{step === 1 ? 'Cancel' : 'Back'}
            </Button>
            {step < 5 && (
              <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="bg-orange-500 hover:bg-orange-600" data-testid="next-step-btn">
                Next <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
